import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Legend, Area, ComposedChart
} from 'recharts';
import { TrendingUp, Plus, Activity, Calendar, AlertCircle, CheckCircle } from 'lucide-react';

export default function BiomarkerTrendChart({ patientId, patientName }) {
  const [availableTests, setAvailableTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState('');
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [biomarkers, setBiomarkers] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [healthSummary, setHealthSummary] = useState(null);
  
  // Form for adding new test result
  const [newResult, setNewResult] = useState({
    test_name: '',
    result_value: '',
    test_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    if (patientId) {
      fetchAvailableTests();
      fetchBiomarkers();
      fetchHealthSummary();
    }
  }, [patientId]);

  useEffect(() => {
    if (selectedTest && patientId) {
      fetchTrendData(selectedTest);
    }
  }, [selectedTest, patientId]);

  const fetchBiomarkers = async () => {
    try {
      const res = await API.get('/patient-trends/biomarkers');
      setBiomarkers(res.data);
    } catch (err) {
      console.error('Failed to fetch biomarkers:', err);
    }
  };

  const fetchAvailableTests = async () => {
    try {
      const res = await API.get(`/patient-trends/patient/${patientId}/available-tests`);
      setAvailableTests(res.data);
      if (res.data.length > 0 && !selectedTest) {
        setSelectedTest(res.data[0].test_name);
      }
    } catch (err) {
      console.error('Failed to fetch available tests:', err);
    }
  };

  const fetchTrendData = async (testName) => {
    setLoading(true);
    try {
      const res = await API.get(`/patient-trends/patient/${patientId}/trend/${encodeURIComponent(testName)}`);
      setTrendData(res.data);
    } catch (err) {
      console.error('Failed to fetch trend data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHealthSummary = async () => {
    try {
      const res = await API.get(`/patient-trends/patient/${patientId}/summary`);
      setHealthSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch health summary:', err);
    }
  };

  const handleAddResult = async () => {
    if (!newResult.test_name || !newResult.result_value) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await API.post('/patient-trends', {
        patient_id: patientId,
        test_name: newResult.test_name,
        result_value: parseFloat(newResult.result_value),
        test_date: newResult.test_date,
        notes: newResult.notes,
        source: 'manual'
      });
      toast.success('Test result added');
      setShowAddDialog(false);
      setNewResult({
        test_name: '',
        result_value: '',
        test_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchAvailableTests();
      fetchHealthSummary();
      if (selectedTest === newResult.test_name) {
        fetchTrendData(selectedTest);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add result');
    }
  };

  // Format data for chart
  const chartData = trendData?.data_points?.map(point => ({
    date: new Date(point.test_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
    fullDate: point.test_date,
    value: point.result_value,
    normalLow: trendData.reference_range?.low,
    normalHigh: trendData.reference_range?.high
  })) || [];

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isNormal = data.normalLow !== null && data.normalHigh !== null && 
                       data.value >= data.normalLow && data.value <= data.normalHigh;
      
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border">
          <p className="font-semibold text-slate-800">{label}</p>
          <p className={`text-lg font-bold ${isNormal ? 'text-green-600' : 'text-red-600'}`}>
            {data.value} {trendData?.reference_range?.unit || ''}
          </p>
          {data.normalLow !== null && data.normalHigh !== null && (
            <p className="text-xs text-slate-500">
              Normal: {data.normalLow} - {data.normalHigh}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Get biomarker categories
  const categorizedBiomarkers = biomarkers.reduce((acc, b) => {
    if (!acc[b.category]) acc[b.category] = [];
    acc[b.category].push(b);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="biomarker-trend-chart">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-teal-600" />
            Clinical Trends
          </h3>
          <p className="text-sm text-slate-500">12-month biomarker history</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} size="sm" data-testid="add-test-result-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Result
        </Button>
      </div>

      {/* Health Summary Cards */}
      {healthSummary && (healthSummary.abnormal_count > 0 || healthSummary.total_tests > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm text-slate-500">Normal</span>
              </div>
              <p className="text-xl font-bold text-green-600">{healthSummary.normal?.length || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <span className="text-sm text-slate-500">High</span>
              </div>
              <p className="text-xl font-bold text-red-600">{healthSummary.abnormal_high?.length || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-orange-600" />
                <span className="text-sm text-slate-500">Low</span>
              </div>
              <p className="text-xl font-bold text-orange-600">{healthSummary.abnormal_low?.length || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-slate-400">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-slate-600" />
                <span className="text-sm text-slate-500">Total Tests</span>
              </div>
              <p className="text-xl font-bold">{healthSummary.total_tests || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Abnormal Values Alert */}
      {healthSummary && (healthSummary.abnormal_high?.length > 0 || healthSummary.abnormal_low?.length > 0) && (
        <Card className="border-red-200 bg-red-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Abnormal Values Detected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {healthSummary.abnormal_high?.map((item, i) => (
                <Badge 
                  key={`high-${i}`} 
                  variant="outline" 
                  className="bg-red-100 text-red-700 border-red-300 cursor-pointer"
                  onClick={() => setSelectedTest(item.test_name)}
                >
                  {item.test_name}: {item.value} {item.unit} (High)
                </Badge>
              ))}
              {healthSummary.abnormal_low?.map((item, i) => (
                <Badge 
                  key={`low-${i}`} 
                  variant="outline" 
                  className="bg-orange-100 text-orange-700 border-orange-300 cursor-pointer"
                  onClick={() => setSelectedTest(item.test_name)}
                >
                  {item.test_name}: {item.value} {item.unit} (Low)
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Selector and Chart */}
      {availableTests.length > 0 ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Trend Analysis</CardTitle>
              <Select value={selectedTest} onValueChange={setSelectedTest}>
                <SelectTrigger className="w-64" data-testid="test-selector">
                  <SelectValue placeholder="Select test" />
                </SelectTrigger>
                <SelectContent>
                  {availableTests.map((test) => (
                    <SelectItem key={test.test_name} value={test.test_name}>
                      {test.test_name} ({test.data_points} readings)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {trendData?.reference_range?.low != null && trendData?.reference_range?.high != null && (
              <CardDescription>
                Normal Range: {trendData.reference_range.low} - {trendData.reference_range.high} {trendData.reference_range.unit}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-64 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : chartData.length > 0 ? (
              <div className="h-72" data-testid="trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <YAxis 
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      axisLine={{ stroke: '#e2e8f0' }}
                      domain={['auto', 'auto']}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    
                    {/* Normal range area */}
                    {trendData?.reference_range?.low != null && trendData?.reference_range?.high != null && (
                      <>
                        <ReferenceLine 
                          y={trendData.reference_range.low} 
                          stroke="#22c55e" 
                          strokeDasharray="5 5"
                          label={{ value: 'Low', fill: '#22c55e', fontSize: 10 }}
                        />
                        <ReferenceLine 
                          y={trendData.reference_range.high} 
                          stroke="#22c55e" 
                          strokeDasharray="5 5"
                          label={{ value: 'High', fill: '#22c55e', fontSize: 10 }}
                        />
                      </>
                    )}
                    
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#0d9488" 
                      strokeWidth={2}
                      dot={{ fill: '#0d9488', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: '#0d9488' }}
                      name={selectedTest}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-500">
                <p>No data available for this test</p>
              </div>
            )}
            
            {/* Data Points Table */}
            {chartData.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Recent Readings</p>
                <div className="flex flex-wrap gap-2">
                  {trendData?.data_points?.slice(-6).reverse().map((point, i) => {
                    const isNormal = trendData?.reference_range?.low != null && 
                                     trendData?.reference_range?.high != null &&
                                     point.result_value >= trendData.reference_range.low && 
                                     point.result_value <= trendData.reference_range.high;
                    return (
                      <div 
                        key={`bio-${i}`} 
                        className={`px-3 py-2 rounded-lg text-sm ${
                          isNormal ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                        }`}
                      >
                        <span className="text-slate-500 text-xs block">
                          {new Date(point.test_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: '2-digit' })}
                        </span>
                        <span className={`font-bold ${isNormal ? 'text-green-700' : 'text-red-700'}`}>
                          {point.result_value} {point.result_unit || ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Activity className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No test results recorded yet</p>
            <Button variant="link" onClick={() => setShowAddDialog(true)}>
              Add the first test result
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add Result Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Test Result</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Test Name *</Label>
              <Select 
                value={newResult.test_name} 
                onValueChange={(v) => setNewResult({ ...newResult, test_name: v })}
              >
                <SelectTrigger data-testid="new-result-test-name">
                  <SelectValue placeholder="Select or type test name" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {Object.entries(categorizedBiomarkers).map(([category, tests]) => (
                    <div key={category}>
                      <p className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50">{category}</p>
                      {tests.map((test) => (
                        <SelectItem key={test.name} value={test.name}>
                          {test.name} ({test.unit})
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Result Value *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newResult.result_value}
                  onChange={(e) => setNewResult({ ...newResult, result_value: e.target.value })}
                  placeholder="e.g., 12.5"
                  data-testid="new-result-value"
                />
              </div>
              <div className="space-y-2">
                <Label>Test Date *</Label>
                <Input
                  type="date"
                  value={newResult.test_date}
                  onChange={(e) => setNewResult({ ...newResult, test_date: e.target.value })}
                  data-testid="new-result-date"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={newResult.notes}
                onChange={(e) => setNewResult({ ...newResult, notes: e.target.value })}
                placeholder="Optional notes..."
              />
            </div>
            
            {/* Show reference range if test selected */}
            {newResult.test_name && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                {(() => {
                  const bio = biomarkers.find(b => b.name === newResult.test_name);
                  return bio ? (
                    <p className="text-slate-600">
                      <span className="font-medium">Normal Range:</span> {bio.low} - {bio.high} {bio.unit}
                    </p>
                  ) : null;
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddResult} data-testid="save-test-result-btn">Save Result</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
